import React, {useContext, useEffect, useRef, useState} from "react";
import { useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import {postClient} from "../../redux/CustomerClientsSlice";
import Button from "@mui/material/Button";
import {
	Alert,
	Dialog, DialogActions, DialogTitle, InputAdornment, Snackbar,
	Step, StepContent, StepLabel, Stepper
} from "@mui/material";
import Typography from "@mui/material/Typography";
import {
	alphaNumericCodeEmptyValidator,
	alphaNumericEmptyValidator,
	alphaNumericValidator,
	checkAllValid, emailValidator, phoneNumberValidator, postcodeValidator
} from "../../Helpers/ValidationHelpers";
import ValidatedTextField from "../../Components/Inputs/ValidatedTextField";
import TrimObjectStrings from "../../Helpers/TrimObjectStrings";
import CountrySelect from "../../Components/Inputs/CountrySelect";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {Link} from "react-router-dom";
import {IsOnboardedContext} from "../../Contexts/IsOnboardedContext";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import LoadingOverlay from "../../Components/Loading/LoadingOverlay";

function CreateClient() {
	useEffect(() => {
		document.title = "New Client - CPC";
	}, []);

	const dispatch = useDispatch();
	const context = useContext(IsOnboardedContext);
	const [activeForm, setActiveForm] = useState({
		createdBy: context?.userName,
		name: "",
		clientCode: "",
		contact1Name: "",
		contact1Email: "",
		contact1Phone: "",
		addressLine1: "",
		addressLine2: "",
		addressCity: "",
		addressState: "",
		addressCountry: "",
		addressPostcode: "",
		registrationNumber: ""
	});
	
	const [clientId, setClientId] = useState(null);
	const [openSubmitDialogue, setOpenSubmitDialogue] = useState(false); 
	const [openSnackBar, setOpenSnackbar] = useState(false); 
	const [activeStep, setActiveStep] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const formValid = useRef({
		basicDetails: {
			name: false,
			clientCode: true,
			registrationNumber: true
		},
		address: {
			addressLine1: true,
			addressLine2: true,
			addressCity: true,
			addressState: true,
			addressCountry: true,
			addressPostcode: true
		},
		contact: {
			contact1Name: true,
			contact1Email: true,
			contact1Phone: true
		}
	});

	const handleNext = () => {
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};

	const handleBack = (step) => {
		setActiveStep((prevActiveStep) => (step !== undefined ? step : prevActiveStep - 1));
	};

	const handleClose = (event, reason) => {
		if (reason === 'clickaway' || reason === 'backdropClick') {
			return;
		}

		setOpenSubmitDialogue(false);
	};

	const handleFailureSnackbarClose = (event, reason) => {
		if (reason === 'clickaway' || reason === 'backdropClick') {
			return;
		}
		setOpenSnackbar(false);
	};
	
	function handleSubmit(event) {
		event.preventDefault();
		if (checkAllValid(formValid.current)) {
			setIsSubmitting(true);
			dispatch(postClient(TrimObjectStrings(activeForm)))
				.unwrap()
				.then((response) => {
					setClientId(response.clientId);
					setOpenSubmitDialogue(true);
					setIsSubmitting(false);
					setActiveForm({
						createdBy: context.userName,
						name: "",
						clientCode: "",
						contact1Name: "",
						contact1Email: "",
						contact1Phone: "",
						addressLine1: "",
						addressLine2: "",
						addressCity: "",
						addressState: "",
						addressCountry: "",
						addressPostcode: "",
						registrationNumber: ""
					});
				})
				.catch((error) => {
					console.error('Failed to post new client', error);
					setIsSubmitting(false);
					setOpenSnackbar(true);
				});
		} 
	}
	
	return (
		<>
			{isSubmitting && <LoadingOverlay />}
			<Typography variant="h2">New Client</Typography>
			<Box
				component="form"
				onSubmit={handleSubmit}
				sx={{
					maxWidth: 600,
					mt: 2,
					'& .MuiTextField-root': { width: '40ch' }
				}}>
				<Stepper activeStep={activeStep} orientation="vertical">
					<Step>
						<StepLabel
							onClick={() => handleBack(0)}
							style={{cursor: activeStep > 0 ? 'pointer' : "default"}}
							className={activeStep > 0 ? "hyperlink" : ""}>
							Basic Details
						</StepLabel>
						<StepContent>
							<Typography>A client is an individual or business to which you sell your products to; your
								customer. Each quote created will be tied to a client.</Typography>
							<Typography sx={{ pt: 1.5 }}>Let's start with a few basic details.</Typography>
							<Box
								display="flex"
								justifyContent="left"
								noValidate
								sx={{ mt: 2 }}
							>
								<ValidatedTextField
									id="name"
									label="Name"
									validator={alphaNumericValidator}
									onChange={isValid => (formValid.current.basicDetails.name = isValid)}
									setActiveForm={setActiveForm}
									initialValue={activeForm?.name}
									fieldPath={['name']}
									infoAdornment={<InputAdornment position="end">
										<Tooltip enterTouchDelay={200} disableFocusListener title={"The client's name."}>
											<InfoIcon />
										</Tooltip>
									</InputAdornment>}
									required
								/>
							</Box>
							<Box
								display="flex"
								justifyContent="left"
								noValidate
								sx={{ mt: 1 }}
							>
								<ValidatedTextField
									id="registrationNumber"
									label="ABN/ACN"
									validator={alphaNumericCodeEmptyValidator}
									onChange={isValid => (formValid.current.basicDetails.registrationNumber = isValid)}
									setActiveForm={setActiveForm}
									initialValue={activeForm?.registrationNumber}
									fieldPath={['registrationNumber']}
									infoAdornment={<InputAdornment position="end">
										<Tooltip enterTouchDelay={200} disableFocusListener title={"The client's Australian Business/Company Number."}>
											<InfoIcon />
										</Tooltip>
									</InputAdornment>}
								/>
							</Box>
							<Box
								display="flex"
								justifyContent="left"
								noValidate
								sx={{ mt: 1 }}
							>
								<ValidatedTextField
									id="clientCode"
									label="Code"
									validator={value => alphaNumericCodeEmptyValidator(value, 0, 6)}
									onChange={isValid => (formValid.current.basicDetails.clientCode = isValid)}
									setActiveForm={setActiveForm}
									initialValue={activeForm?.clientCode}
									fieldPath={['clientCode']}
									infoAdornment={<InputAdornment position="end">
										<Tooltip enterTouchDelay={200} disableFocusListener title={"Provide an abbreviated code (up to 6 characters) to represent the client if you wish."}>
											<InfoIcon />
										</Tooltip>
									</InputAdornment>}
								/>
							</Box>
							<Box sx={{ my: 2 }}>
								<Button
									variant="contained"
									onClick={handleNext}
									sx={{ mt: 1, mr: 1 }}
									disabled={!checkAllValid(formValid.current.basicDetails)}
								>
									Continue
								</Button>
							</Box>
						</StepContent>
					</Step>
					<Step>
						<StepLabel
							onClick={() => { if (activeStep > 1) { handleBack(); } }}
							style={{cursor: activeStep > 1 ? 'pointer' : "default"}}
							optional={<Typography variant="caption">Optional</Typography>}
							className={activeStep > 1 ? "hyperlink" : ""}>
							Address
						</StepLabel>
						<StepContent>
							<Typography>Next, let's add some address details.</Typography>
							<Box
								display="flex"
								justifyContent="left"
								noValidate
								sx={{ mt: 2 }}
							>
								<ValidatedTextField
									id="addressLine1"
									label="Line 1"
									validator={alphaNumericValidator}
									onChange={isValid => (formValid.current.address.addressLine1 = isValid)}
									setActiveForm={setActiveForm}
									initialValue={activeForm?.addressLine1}
									fieldPath={['addressLine1']}
									infoAdornment={null}
								/>
							</Box>
							<Box
								display="flex"
								justifyContent="left"
								noValidate
								sx={{ mt: 1 }}
							>
								<ValidatedTextField
									id="addressLine2"
									label="Line 2"
									validator={alphaNumericEmptyValidator}
									onChange={isValid => (formValid.current.address.addressLine2 = isValid)}
									setActiveForm={setActiveForm}
									initialValue={activeForm?.addressLine2}
									fieldPath={['addressLine2']}
									infoAdornment={null}
								/>
							</Box>
							<Box
								display="flex"
								justifyContent="left"
								noValidate
								sx={{ mt: 1 }}
							>
								<ValidatedTextField
									id="addressCity"
									label="City"
									validator={alphaNumericEmptyValidator}
									onChange={isValid => (formValid.current.address.addressCity = isValid)}
									setActiveForm={setActiveForm}
									initialValue={activeForm?.addressCity}
									fieldPath={['addressCity']}
									infoAdornment={null}
								/>
							</Box>
							<Box
								display="flex"
								justifyContent="left"
								noValidate
								sx={{ mt: 1 }}
							>
								<ValidatedTextField
									id="addressState"
									label="State"
									validator={alphaNumericEmptyValidator}
									onChange={isValid => (formValid.current.address.addressState = isValid)}
									setActiveForm={setActiveForm}
									initialValue={activeForm?.addressState}
									fieldPath={['addressState']}
									infoAdornment={null}
								/>
							</Box>
							<Box
								display="flex"
								justifyContent="left"
								noValidate
								sx={{ mt: 1 }}
							>
								<ValidatedTextField
									id="addressPostcode"
									label="Postcode"
									validator={postcodeValidator}
									onChange={isValid => (formValid.current.address.addressPostcode = isValid)}
									setActiveForm={setActiveForm}
									initialValue={activeForm?.addressPostcode}
									fieldPath={['addressPostcode']}
									infoAdornment={null}
								/>
							</Box>
							<Box
								display="flex"
								justifyContent="left"
								noValidate
								sx={{ mt: 1 }}
							>
								<CountrySelect
									setActiveForm={setActiveForm}
									onChange={isValid => (formValid.current.addressCountry = isValid)}
									initialValue={activeForm.addressCountry}
								/>
							</Box>
							<Box sx={{ my: 2 }}>
								<Button
									variant="contained"
									onClick={handleNext}
									sx={{ mt: 1, mr: 1 }}
									disabled={!checkAllValid(formValid.current.address)}
								>
									Continue
								</Button>
								<Button
									onClick={() => handleBack(0)}
									sx={{ mt: 1, mr: 1 }}
								>
									Back
								</Button>
							</Box>
						</StepContent>
					</Step>
					<Step>
						<StepLabel
							optional={<Typography variant="caption">Optional</Typography>}
						>
							Primary Contact
						</StepLabel>
						<StepContent>
							<Typography>Finally, supply the details of the primary contact of the client.</Typography>
							<Box
								display="flex"
								justifyContent="left"
								noValidate
								sx={{ mt: 2 }}
							>
								<ValidatedTextField
									id="contact1Name"
									label="Name"
									validator={alphaNumericValidator}
									onChange={isValid => (formValid.current.contact.contact1Name = isValid)}
									setActiveForm={setActiveForm}
									initialValue={activeForm?.contact1Name}
									fieldPath={['contact1Name']}
									infoAdornment={<InputAdornment position="end">
										<Tooltip disableFocusListener title={"The name of the contact."}>
											<InfoIcon />
										</Tooltip>
									</InputAdornment>}
								/>
							</Box>
							<Box
								display="flex"
								justifyContent="left"
								noValidate
								sx={{ mt: 2 }}
							>
								<ValidatedTextField
									id="contact1Email"
									label="Email"
									validator={emailValidator}
									onChange={isValid => (formValid.current.contact.contact1Email = isValid)}
									setActiveForm={setActiveForm}
									initialValue={activeForm?.contact1Email}
									fieldPath={['contact1Email']}
									infoAdornment={<InputAdornment position="end">
									<Tooltip disableFocusListener title={"The email address of the contact."}>
										<InfoIcon />
									</Tooltip>
								</InputAdornment>}
								/>
							</Box>
							<Box
								display="flex"
								justifyContent="left"
								noValidate
								sx={{ mt: 2 }}
							>
								<ValidatedTextField
									id="contact1Phone"
									label="Phone Number"
									validator={phoneNumberValidator}
									onChange={isValid => (formValid.current.contact.contact1Phone = isValid)}
									setActiveForm={setActiveForm}
									initialValue={activeForm?.contact1Phone}
									fieldPath={['contact1Phone']}
									infoAdornment={<InputAdornment position="end">
										<Tooltip disableFocusListener title={"The phone number of the contact."}>
											<InfoIcon />
										</Tooltip>
									</InputAdornment>}
								/>
							</Box>
							<Box sx={{ my: 2 }}>
								<Button
									variant="contained"
									onClick={handleSubmit}
									sx={{ mt: 1, mr: 1 }}
									disabled={!checkAllValid(formValid.current) || isSubmitting}
								>
									{isSubmitting ? "Submitting..." : "Finish"}
								</Button>
								<Button
									onClick={() => handleBack()}
									sx={{ mt: 1, mr: 1 }}
									disabled={isSubmitting}
								>
									Back
								</Button>
							</Box>
						</StepContent>
					</Step>
				</Stepper>
			</Box>
			<Dialog
				open={openSubmitDialogue}
				keepMounted
				onClose={handleClose}
				aria-describedby="alert-successful-client-creation"
			>
				<DialogTitle>
					<Box display="flex" alignItems="center">
						<CheckCircleIcon color="success" fontSize="large" sx={{
							marginRight: '15px'
						}}/>
						<Typography variant="h6">{`Client successfully created!`}</Typography>
					</Box>
				</DialogTitle>
				<DialogActions>
					<Link to={`/`}>
						<Button onClick={handleClose}>
							{`Home`}
						</Button>
					</Link>
					<Link to={`/Clients/CreateNewClient`} reloadDocument>
						<Button onClick={handleClose}>
							{`Create another`}
						</Button>
					</Link>
					<Link to={`/Clients/${clientId}`} underline="none">
						<Button onClick={handleClose} variant="contained" color="primary">
							{`Go to client`}
						</Button>
					</Link>

				</DialogActions>
			</Dialog>
			<Snackbar open={openSnackBar}
					  onClose={handleFailureSnackbarClose}
					  autoHideDuration={3000}
			>
				<Alert onClose={handleFailureSnackbarClose}
					   severity="error"
				>
					Failed to create client. Please try again.
				</Alert>
			</Snackbar>
		</>
	);
}

export default CreateClient;
